import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { MapPin } from "lucide-react";

/**
 * A location pin that drops onto a small stylized map backdrop
 * when the section enters the viewport. The heading + description
 * fade in from below once the pin lands. Used to introduce
 * category sections like Vehicles, Trailers and Equipment.
 */
const PinDropHeader = ({ eyebrow, title, desc, align = "left", dark = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20% 0px" });

  return (
    <div ref={ref} className={`relative ${align === "center" ? "text-center" : "text-left"}`}>
      {/* Map tile backdrop + pin — contained in a floating square */}
      <div className={`relative mb-6 inline-flex ${align === "center" ? "mx-auto" : ""}`}>
        <div className="relative h-20 w-20 overflow-hidden rounded-2xl shadow-subtle sm:h-24 sm:w-24">
          {/* Faux map background */}
          <svg viewBox="0 0 96 96" className="absolute inset-0 h-full w-full">
            <rect width="96" height="96" fill="#F3EFE8" />
            <rect x="6" y="12" width="34" height="22" rx="2" fill="#E8DFCF" />
            <rect x="44" y="12" width="46" height="22" rx="2" fill="#E8DFCF" />
            <rect x="6" y="38" width="42" height="26" rx="2" fill="#E8DFCF" />
            <rect x="52" y="38" width="38" height="26" rx="2" fill="#E8DFCF" />
            <rect x="6" y="68" width="48" height="22" rx="2" fill="#E8DFCF" />
            <rect x="58" y="68" width="32" height="22" rx="2" fill="#E8DFCF" />
            <path d="M0 36 H96" stroke="#F6EBD1" strokeWidth="3" opacity="0.6" />
            <path d="M0 66 H96" stroke="#F6EBD1" strokeWidth="3" opacity="0.6" />
            <path d="M42 0 V96" stroke="#F6EBD1" strokeWidth="3" opacity="0.6" />
          </svg>

          {/* Pin — drops in and bounces */}
          <motion.div
            initial={{ y: -56, opacity: 0, scale: 0.6 }}
            animate={inView ? { y: 0, opacity: 1, scale: 1 } : {}}
            transition={{ type: "spring", stiffness: 220, damping: 15, delay: 0.1 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          >
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow-glow"
              style={{ background: "var(--brand-accent)" }}
            >
              <MapPin className="h-4 w-4" strokeWidth={2.8} fill="currentColor" fillOpacity="0.25" />
            </div>
            {/* Landing pulse */}
            <motion.span
              initial={{ opacity: 0, scale: 0.5 }}
              animate={inView ? { opacity: [0, 0.4, 0], scale: [0.5, 2, 2.4] } : {}}
              transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
              className="absolute left-1/2 top-1/2 -z-10 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ background: "var(--brand-accent)" }}
            />
          </motion.div>
        </div>
      </div>

      {/* Eyebrow + heading + desc, fade in after pin lands */}
      {eyebrow && (
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-[11px] font-black uppercase tracking-[0.22em]"
          style={{ color: "var(--brand-accent)" }}
        >
          {eyebrow}
        </motion.p>
      )}
      <motion.h2
        initial={{ opacity: 0, y: 10 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.55, delay: 0.62 }}
        className={`mt-2 font-heading text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl ${
          dark ? "text-white" : "text-tracksy-ink"
        }`}
      >
        {title}
      </motion.h2>
      {desc && (
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55, delay: 0.74 }}
          className={`mt-4 max-w-xl text-sm leading-relaxed sm:text-base ${
            align === "center" ? "mx-auto" : ""
          } ${dark ? "text-white/80" : "text-tracksy-sub"}`}
        >
          {desc}
        </motion.p>
      )}
    </div>
  );
};

export default PinDropHeader;
